// src/sections/employer-data/shared/utils/policyUtils.tsx

import type { PolicyNameViewModel } from 'src/api/types/PolicyNameViewModel';

/**
 * Gets the primary policy name based on the following priority:
 * 1. NameLinkNumber === "001" AND ContinuationSequenceNumber === "001"
 * 2. If multiple matches, select the one with most recent NameEffectiveDate
 * 3. If no "001" continuation sequence, try ContinuationSequenceNumber === "002"
 * 4. If no continuation sequence exists, return any record with NameLinkNumber === "001"
 *
 * @param policyNames - Array of policy names from the policy
 * @returns The primary policy name or undefined if no match found
 */
export const getPrimaryPolicyName = (
  policyNames?: PolicyNameViewModel[]
): PolicyNameViewModel | undefined => {
  if (!policyNames || policyNames.length === 0) {
    return undefined;
  }

  // Filter for NameLinkNumber === "001"
  const nameLinkMatches = policyNames.filter((pn) => pn.nameLinkNumber === '001');

  if (nameLinkMatches.length === 0) {
    return undefined;
  }

  // Try to find with ContinuationSequenceNumber === "001"
  const continuation001 = nameLinkMatches.filter((pn) => pn.continuationSequenceNumber === '001');

  if (continuation001.length > 0) {
    // If multiple matches, get the one with most recent PolicyChangeEffectiveDate
    return getMostRecentByEffectiveDate(continuation001);
  }

  // Fallback to ContinuationSequenceNumber === "002"
  const continuation002 = nameLinkMatches.filter((pn) => pn.continuationSequenceNumber === '002');

  if (continuation002.length > 0) {
    return getMostRecentByEffectiveDate(continuation002);
  }

  // Fallback to any record with NameLinkNumber === "001" (no continuation sequence filter)
  return getMostRecentByEffectiveDate(nameLinkMatches);
};

/**
 * Helper function to get the record with the most recent PolicyChangeEffectiveDate
 * @param records - Array of policy name records
 * @returns The record with most recent effective date
 */
const getMostRecentByEffectiveDate = (
  records: PolicyNameViewModel[]
): PolicyNameViewModel | undefined => {
  if (records.length === 0) {
    return undefined;
  }

  if (records.length === 1) {
    return records[0];
  }

  // Sort by nameEffectiveDate descending (most recent first)
  return records.sort((a, b) => {
    const dateA = a.nameEffectiveDate ? new Date(a.nameEffectiveDate).getTime() : 0;
    const dateB = b.nameEffectiveDate ? new Date(b.nameEffectiveDate).getTime() : 0;
    return dateB - dateA;
  })[0];
};
